// src/context/LinkedAccountsContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useUser } from "./UserContext";
import { LinkedAccounts, LinkedAccountsContextType } from "../types";
import { api } from "../services/api";
import { User } from "lucide-react";
const LinkedAccountsContext = createContext<
  LinkedAccountsContextType | undefined
>(undefined);

export const LinkedAccountsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { loading: userLoading, user } = useUser(); // no token now
  const [accounts, setAccounts] = useState<LinkedAccounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [discordMembership, setDiscordMembership] = useState<{
    linked: boolean;
    isMember: boolean;
  } | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);

  const refreshLinkedAccounts = async () => {
    try {
      const res = await api.app.getLinkedAccounts();
      setAccounts(res.linked || {});
    } catch (err) {
      console.error("LinkedAccounts fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshMembership = async () => {
    if (!user) {
      setDiscordMembership(null);
      setMembershipError(null);
      setMembershipLoading(false);
      return;
    }
    setMembershipLoading(true);
    try {
      const res = await api.app.getDiscordMembership();
      setDiscordMembership({ linked: res.linked, isMember: res.isMember });
      setMembershipError(res.error ?? null);
    } catch (err) {
      console.error("Discord membership fetch error:", err);
      setMembershipError("unresolved");
    } finally {
      setMembershipLoading(false);
    }
  };

  const rejoinDiscord = async () => {
    const res = await api.app.rejoinDiscord();
    // Only refresh when the rejoin actually resolved membership; a reauth or
    // error response leaves membership untouched and is handled by the caller.
    if (!res.error && !res.reauthRequired) {
      await refreshMembership();
    }
    return res;
  };

  // Automatically fetch when user changes (login/logout)
  useEffect(() => {
    if (userLoading) { setLoading(false); return; }
    const fetchLinkedAccounts = async () => {
      try {
        refreshLinkedAccounts();
      } catch (err) {
        console.error("Failed to fetch linked accounts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkedAccounts();
    refreshMembership();
  }, [user]);

  return (
    <LinkedAccountsContext.Provider
      value={{
        accounts,
        loading,
        refreshLinkedAccounts,
        discordMembership,
        membershipLoading,
        membershipError,
        refreshMembership,
        rejoinDiscord,
      }}
    >
      {children}
    </LinkedAccountsContext.Provider>
  );
};

export const useLinkedAccounts = () => {
  const context = useContext(LinkedAccountsContext);
  if (!context) {
    throw new Error(
      "useLinkedAccounts must be used within a LinkedAccountsProvider"
    );
  }
  return context;
};
